import { useMemo } from "react";
import type {
	ClientPortfolioProducerInstrumentResponseDTO,
	ClientPortfolioToolReportingSummaryResponseDTO,
} from "web_myplann/Infrastructure/Api/Api";
import { useResource } from "web_myplann/Infrastructure/Translations/Resources";
import type { FundDetailSelection } from "web_myplann/Portfolio/Components/Redesign/Detail/FundDetail/FundDetailDrawer";
import { formatFundValue } from "web_myplann/Portfolio/Helpers/formatFundValue";
import type { NormalizedTool } from "web_myplann/Portfolio/Helpers/investmentToolTypes";
import { moneyFormatter } from "web_myplann/Shared/Utils/moneyFormatters";

export type FundRow = {
	key: string;
	label: string;
	value: string;
	selection: FundDetailSelection;
};

type Params = {
	tools: NormalizedTool[];
	producerInstruments?: ClientPortfolioProducerInstrumentResponseDTO[] | null;
	toolsSummary?: ClientPortfolioToolReportingSummaryResponseDTO[];
	currencySign: string;
};

export const useFundRows = ({
	tools,
	producerInstruments,
	toolsSummary,
	currencySign,
}: Params): FundRow[] => {
	const { t } = useResource();

	return useMemo(() => {
		const hasProducerInstruments =
			producerInstruments && producerInstruments.length > 0;

		if (hasProducerInstruments) {
			return producerInstruments.map((instrument) => ({
				key: instrument.isin,
				label: instrument.name ?? instrument.isin,
				value:
					instrument.currentValue === null ||
					instrument.currentValue === undefined
						? ""
						: moneyFormatter(
								instrument.currentValue,
								currencySign,
								undefined,
								0,
							),
				selection: {
					kind: "producerInstrument",
					instrument,
				},
			}));
		}

		return tools.map((tool) => ({
			key: tool.id.toString(),
			label: tool.name ?? "",
			value: formatFundValue(toolsSummary, tool.id, t),
			selection: {
				kind: "tool",
				toolId: tool.id,
				name: tool.name ?? "",
			},
		}));
	}, [tools, producerInstruments, toolsSummary, currencySign, t]);
};
